import { useCallback } from 'react';
import { SdkErrorResponse } from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';
import { CorpUIResponse } from 'src/types/Corp';
import { CorporateFormFields } from 'src/components/enterprise/features/Corporate/components/Form/types/CorporateForm';
import { executeCorporateUpdate } from 'src/components/enterprise/features/Corporate/EditPage/functions/corporateUpdate';
import { updateCorporateModel } from 'src/components/enterprise/features/Corporate/utils/corporate.utilities';
import { transformCateringServiceFeeUIToModel } from 'src/components/enterprise/features/Corporate/components/Form/CateringServiceFeeControl/cateringServiceFeeControl.utils';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';

export type UseUpdateCorporateProps = {
  corpId: string;
  corpRawData: CorpUIResponse;
  onSuccess: () => Promise<void>;
  onError: (error: IErrorResponse | SdkErrorResponse) => Promise<void>;
};

export type UseUpdateCorporateReturnType = {
  updateCorp: (values: CorporateFormFields) => Promise<void>;
};

export const useUpdateCorporate = ({
  corpId,
  corpRawData,
  onSuccess,
  onError,
}: UseUpdateCorporateProps): UseUpdateCorporateReturnType => {
  const showSsoOnNativeOption = useFeatureFlag(
    FeatureFlag.SHOW_SSO_ON_NATIVE_OPTION
  );
  const updateCorpTrigger = useCallback(
    async (values: CorporateFormFields) => {
      const { cateringServiceFee } = values;
      // Preserve existing redirect settings when the feature is off and the controls are not rendered
      // Will be removed when the feature is permanently enabled

      const ssoRedirectEnabled =
        showSsoOnNativeOption.value && values.ssoEnabled
          ? values.ssoRedirectEnabled
          : corpRawData.ssoRedirectEnabled;
      const ssoRedirectUrl =
        showSsoOnNativeOption.value && values.ssoEnabled
          ? values.ssoRedirectUrl
          : corpRawData.ssoRedirectUrl;

      const corporatePayload = updateCorporateModel(
        {
          ...values,
          ssoRedirectEnabled,
          ssoRedirectUrl,
        },
        corpRawData
      );
      const cateringServiceFeePayload =
        transformCateringServiceFeeUIToModel(cateringServiceFee);

      const shouldEnableUniversalAccounts =
        !corpRawData.universalAccountsEnabled &&
        values.universalAccountsEnabled;

      try {
        await executeCorporateUpdate(
          corpId,
          corporatePayload,
          cateringServiceFeePayload,
          shouldEnableUniversalAccounts
        );
        await onSuccess();
      } catch (error) {
        await onError(error);
      }
    },
    [corpId, corpRawData, onSuccess, onError, showSsoOnNativeOption]
  );

  return {
    updateCorp: updateCorpTrigger,
  };
};
